import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import LoadingButton from '@mui/lab/LoadingButton';
import { alpha, useTheme } from '@mui/material/styles';
import InputAdornment from '@mui/material/InputAdornment';

import { bgGradient } from '../../theme/css';
import Iconify from '../../theme/iconify/iconify';

import Language from '../util/Language';
import { mislecAPI } from '../../util/axios';
import Notification from '../util/Notification';

function Register() {
    const [token, setToken] = React.useState(sessionStorage.getItem('mISLec_access_token') || null);
    const [fname, setFname] = React.useState('');
    const [lname, setLname] = React.useState('');
    const [username, setUsername] = React.useState('');
    const [email, setEmail] = React.useState('');
    const [emailError, setEmailError] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [showPassword, setShowPassword] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [message, setMessage] = React.useState(null);
    const [severity, setSeverity] = React.useState(null);

    const theme = useTheme();
    const { i18n, t } = useTranslation();
    const navigate = useNavigate();

    React.useEffect(() => {
        if (token) navigate('/');
    }, [token]);

    const register = () => {
        //  Timestamp
        const timestamp = new Date().toISOString().replace(/T/, ' ').replace(/\..+/, '');

        //  Request body
        const data = {
            fname: fname,
            lname: lname,
            username: username,
            email: email,
            password: password,
            registration_date: timestamp,
            language: i18n.language
        }

        //  Validate data
        if (!data.fname || !data.lname || !data.username || !data.email || !data.password) {
            setOpen(true);
            setMessage(t('register.error.blank'));
            setSeverity('warning');
            return;
        }

        //  E-mail validation
        if (!!emailError) {
            setOpen(true);
            setMessage(t('register.error.email'));
            setSeverity('warning');
            return;
        }

        //  Send data to server
        mislecAPI.post(`/api/auth/register`, data)
            .then(res => {
                setMessage(t(res.data.detail));
                setSeverity('success');
                setOpen(true);
                setTimeout(() => {
                    navigate('/login');
                }, 2500);
            })
            .catch(error => {
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);
            });
    }

    const handleKeyDown = (event) => {
        if (event.target.type === 'button') return;

        if (event.key === 'Enter') {
            event.preventDefault();
            register();
        }
    }

    const handleEmail = (e) => {
        setEmail(e.target.value);

        if (e.target.value && !validateEmail(e.target.value)) {
            setEmailError(t('register.error.email'));
        } else {
            setEmailError('');
        }
    }

    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return re.test(email);
    }

    const renderForm = (
        <>
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <TextField
                        id='mISLec_fname'
                        name='fname'
                        label={t('register.inputs.fname.label')}
                        value={fname}
                        onChange={(e) => setFname(e.target.value)}
                        onKeyDown={handleKeyDown}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        id='mISLec_lname'
                        name='lname'
                        label={t('register.inputs.lname.label')}
                        value={lname}
                        onChange={(e) => setLname(e.target.value)}
                        onKeyDown={handleKeyDown}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        id='mISLec_username'
                        name='username'
                        label={t('register.inputs.username.label')}
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        onKeyDown={handleKeyDown}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        id='mISLec_email'
                        name='email'
                        label={t('register.inputs.email.label')}
                        value={email}
                        onChange={(e) => handleEmail(e)}
                        error={!!emailError}
                        helperText={emailError}
                        onKeyDown={handleKeyDown}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        id='mISLec_password'
                        name='password'
                        label={t('register.inputs.password.label')}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyDown={handleKeyDown}
                        type={showPassword ? 'text' : 'password'}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position='end' sx={{ mr: 1 }}>
                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge='end' sx={{ borderRadius: '8px' }}>
                                        <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12}>
                    <Stack sx={{ my: 3 }}>
                        <LoadingButton
                            fullWidth
                            size='large'
                            type='submit'
                            variant='contained'
                            color='inherit'
                            onClick={() => register()}
                        >
                            {t('register.buttons.register')}
                        </LoadingButton>
                    </Stack>
                </Grid>
            </Grid>
        </>
    );

    return (
        <>
            <Box
                sx={{
                    ...bgGradient({
                        color: alpha(theme.palette.background.default, 0.9),
                        imgUrl: '/assets/background/overlay_4.jpg',
                    }),
                    height: 1,

                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: { xs: 0, md: 16 },
                        left: 0,
                        right: 0,
                        pt: { xs: 1, md: 0 },
                        px: { xs: 1, md: 2 },
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Tooltip title={t('header.home')} arrow>
                        <Button
                            variant=''
                            onClick={() => navigate('/')}
                            sx={{ p: 0.5, minWidth: 'auto' }}
                        >
                            <img
                                src='/assets/images/mISLec-logo.png'
                                alt={t('common.logo_alt')}
                                style={{ width: '56px', height: 'auto', maxWidth: '56px' }}
                            />
                        </Button>
                    </Tooltip>
                    <Language />
                </Box>
                <Stack alignItems='center' justifyContent='center' sx={{ height: 1 }}>
                    <Card
                        sx={{
                            p: 5,
                            width: 1,
                            maxWidth: {
                                xs: '95%',
                                md: 420
                            }
                        }}
                    >
                        <Typography variant='h4'>{t('register.title')}</Typography>

                        <Typography variant='body2' sx={{ mt: 2, mb: 5 }}>
                            {t('register.description')}
                            <Link to='/login' style={{ marginLeft: '4px', textDecoration: 'none' }}>
                                <Typography
                                    variant='body2'
                                    sx={{
                                        display: 'inline',
                                        color: 'primary.main',
                                        fontWeight: '600',
                                        textDecoration: 'none !important',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {t('register.links.login')}
                                </Typography>
                            </Link>
                            <span>.</span>
                        </Typography>

                        {renderForm}
                    </Card>
                </Stack>
            </Box>
            <Notification open={open} message={message} severity={severity} onClose={() => setOpen(false)} />
        </>
    );
}

export default Register;